import type { BarcodeDetector as BarcodeDetectorPonyfill } from "barcode-detector/ponyfill";

export interface QrDetector {
  detect(source: ImageBitmapSource): Promise<{ rawValue: string }[]>;
}

type BarcodeDetectorConstructor = typeof BarcodeDetectorPonyfill;

let pending: Promise<QrDetector> | null = null;

async function createDetector(): Promise<QrDetector> {
  // Android Chrome ships ML Kit behind BarcodeDetector, and its rawValue is the payload untouched.
  const Native = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
  if (Native) {
    try {
      const formats = await Native.getSupportedFormats();
      if (formats.includes("qr_code")) return new Native({ formats: ["qr_code"] });
    } catch {
      // Fall through to the WebAssembly reader.
    }
  }

  // iOS Safari, Firefox and desktop Chrome lack it. The reader's .wasm is served from our own origin — the
  // library would otherwise fetch it from a public CDN.
  const [{ BarcodeDetector, setZXingModuleOverrides }, { default: wasmUrl }] = await Promise.all([
    import("barcode-detector/ponyfill"),
    import("zxing-wasm/reader/zxing_reader.wasm?url"),
  ]);
  setZXingModuleOverrides({
    locateFile: (path: string, prefix: string) => (path.endsWith(".wasm") ? wasmUrl : prefix + path),
  });
  return new BarcodeDetector({ formats: ["qr_code"] });
}

/** One detector per page load; a failed load is forgotten so the next attempt retries it. */
export function loadQrDetector(): Promise<QrDetector> {
  if (!pending) {
    pending = createDetector().catch((error: unknown) => {
      pending = null;
      throw error;
    });
  }
  return pending;
}

/** Every QR in a picture (a screenshot, a photo of an invoice); empty when there is none. */
export async function detectQrInImage(file: Blob): Promise<string[]> {
  const detector = await loadQrDetector();
  const bitmap = await createImageBitmap(file);
  try {
    const codes = await detector.detect(bitmap);
    return codes.map((code) => code.rawValue);
  } finally {
    bitmap.close();
  }
}
